import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, History, Shield, User, Clock, AlertTriangle, Mail, KeyRound,
  Ban, PlayCircle, Trash2, Edit2, UserCheck, UserX, Loader2, Info, Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

// Map common action types → icon + colour for a fast-scan timeline.
const ACTION_META = {
  // Account lifecycle
  staff_account_created:          { Icon: UserCheck, hex: '#10B981', label: 'Created' },
  staff_account_deleted:          { Icon: Trash2,    hex: '#F43F5E', label: 'Deleted' },
  staff_account_updated:          { Icon: Edit2,     hex: '#F59E0B', label: 'Profile updated' },
  staff_account_suspended:        { Icon: Ban,       hex: '#F43F5E', label: 'Suspended' },
  staff_account_unsuspended:      { Icon: PlayCircle,hex: '#10B981', label: 'Reactivated' },
  staff_role_changed:             { Icon: Shield,    hex: '#8A2BE2', label: 'Role changed' },
  password_reset_by_admin:        { Icon: KeyRound,  hex: '#F59E0B', label: 'Password reset by admin' },
  staff_password_reset:           { Icon: KeyRound,  hex: '#F59E0B', label: 'Password reset' },
  user_staff_role_changed:        { Icon: Shield,    hex: '#8A2BE2', label: 'Moderator role changed' },
  user_account_deleted:           { Icon: Trash2,    hex: '#F43F5E', label: 'User deleted' },
  // Security
  login_success:                  { Icon: UserCheck, hex: '#10B981', label: 'Signed in' },
  login_failed:                   { Icon: UserX,     hex: '#F43F5E', label: 'Failed login' },
  logout:                         { Icon: User,      hex: '#94A3B8', label: 'Signed out' },
  privilege_escalation_attempt:   { Icon: AlertTriangle, hex: '#F43F5E', label: 'Escalation attempt' },
  ip_locked:                      { Icon: Shield,    hex: '#F43F5E', label: 'IP locked' },
  ip_unlocked:                    { Icon: Shield,    hex: '#10B981', label: 'IP unlocked' },
  '2fa_enabled':                  { Icon: Shield,    hex: '#10B981', label: '2FA enabled' },
  '2fa_disabled':                 { Icon: Shield,    hex: '#F43F5E', label: '2FA disabled' },
  // Comms
  admin_message_sent:             { Icon: Mail,      hex: '#00E5FF', label: 'Inbox message sent' },
};

const prettyAction = (a) => {
  if (!a) return 'Unknown';
  const meta = ACTION_META[a];
  if (meta) return meta.label;
  return a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const sevHex = (s) => ({ critical: '#F43F5E', warning: '#F59E0B', info: '#94A3B8' })[s] || '#94A3B8';

/**
 * Slide-in drawer showing every admin/audit action against a target user.
 * Works for both staff (by username) and public users (by user_id).
 */
export const ActionLogDrawer = ({ scope, identifier, displayName, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | security | account | comms
  const [exporting, setExporting] = useState(false);

  const exportPDF = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('staff_token') || localStorage.getItem('token');
      const path = scope === 'user'
        ? `${BACKEND_URL}/api/admin/user-accounts/${identifier}/action-log/export`
        : `${BACKEND_URL}/api/admin/staff-accounts/${identifier}/action-log/export`;
      const res = await axios.get(path, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `action-log-${identifier}-${today}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to export PDF');
    }
    setExporting(false);
  };

  useEffect(() => {
    if (!identifier) return;
    const load = async () => {
      setLoading(true);
      try {
        const h = getAuthHeaders();
        const path = scope === 'user'
          ? `${BACKEND_URL}/api/admin/user-accounts/${identifier}/action-log`
          : `${BACKEND_URL}/api/admin/staff-accounts/${identifier}/action-log`;
        const res = await axios.get(path, { headers: h });
        setEntries(res.data.entries || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load action log');
      }
      setLoading(false);
    };
    load();
  }, [identifier, scope]);

  if (!identifier) return null;

  const filtered = entries.filter(e => {
    if (filter === 'all') return true;
    const a = (e.action || '').toLowerCase();
    if (filter === 'security')
      return /login|logout|2fa|ip_|privilege|security|password|lock/.test(a);
    if (filter === 'account')
      return /account|staff_|role|suspend|unsuspend|delet|updat|creat/.test(a);
    if (filter === 'comms')
      return /message|notif|inbox|email|newsletter/.test(a);
    return true;
  });

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="action-log-backdrop" />
      <div className="fixed inset-y-0 right-0 z-[101] w-full max-w-xl bg-gradient-to-br from-[#0A0A14] via-[#0D0B1F] to-[#0A0A14] border-l border-cyan-500/20 shadow-[0_0_60px_-15px_rgba(0,229,255,0.35)] overflow-hidden flex flex-col"
        style={{ animation: 'slideInRight 0.25s ease-out' }} data-testid="action-log-drawer">
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div className="relative overflow-hidden border-b border-white/[0.06] p-5">
          <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-cyan-500/[0.10] blur-[100px]" />
          <div className="relative flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center flex-shrink-0">
              <History className="w-5 h-5 text-cyan-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.24em] uppercase text-cyan-300/70 font-medium">Admin action log</p>
              <h2 className="text-xl font-semibold text-white tracking-tight truncate">{displayName || identifier}</h2>
              <p className="text-white/40 text-xs mt-0.5">{entries.length} event{entries.length === 1 ? '' : 's'} recorded</p>
            </div>
            <button onClick={exportPDF} disabled={exporting || entries.length === 0} data-testid="action-log-export-pdf"
              title="Export as PDF (GDPR / audit)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/[0.10] border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/[0.20] disabled:opacity-40 disabled:cursor-not-allowed text-[11px] font-semibold transition-all flex-shrink-0">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              PDF
            </button>
            <button onClick={onClose} data-testid="action-log-close"
              className="w-8 h-8 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Filter chips */}
          <div className="relative mt-4 inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
            {[
              { id: 'all',      label: 'All',       count: entries.length },
              { id: 'security', label: 'Security',  count: entries.filter(e => /login|logout|2fa|ip_|privilege|security|password|lock/.test((e.action || '').toLowerCase())).length },
              { id: 'account',  label: 'Account',   count: entries.filter(e => /account|staff_|role|suspend|unsuspend|delet|updat|creat/.test((e.action || '').toLowerCase())).length },
              { id: 'comms',    label: 'Comms',     count: entries.filter(e => /message|notif|inbox|email|newsletter/.test((e.action || '').toLowerCase())).length },
            ].map(t => {
              const a = filter === t.id;
              return (
                <button key={t.id} onClick={() => setFilter(t.id)}
                  data-testid={`action-log-filter-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] font-medium transition-all ${
                    a ? 'text-white bg-white/[0.06] border border-white/20' : 'text-white/50 hover:text-white border border-transparent'
                  }`}>
                  {t.label}
                  <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${a ? 'bg-cyan-500/25 text-cyan-200' : 'bg-white/[0.04] text-white/45'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto jewel-scroll p-5">
          {loading ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 className="w-5 h-5 text-cyan-300 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-14">
              <Info className="w-7 h-7 text-white/20 mx-auto mb-2" />
              <p className="text-white/40 text-sm">No events recorded</p>
              <p className="text-white/25 text-xs mt-1">Admin actions on this account will appear here.</p>
            </div>
          ) : (
            <div className="relative pl-5 space-y-3 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-white/[0.06]">
              {filtered.map((e) => {
                const meta = ACTION_META[e.action] || { Icon: Info, hex: sevHex(e.severity), label: prettyAction(e.action) };
                const Icon = meta.Icon;
                const hex = meta.hex;
                const details = e.details || {};
                return (
                  <div key={e.id} className="relative" data-testid={`action-log-entry-${e.id}`}>
                    <div className="absolute -left-[13px] top-2 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center bg-[#0a0a14]"
                      style={{ borderColor: hex }}>
                      <Icon className="w-2.5 h-2.5" style={{ color: hex }} />
                    </div>
                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3 hover:border-white/[0.14] transition-all">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-white text-[12.5px] font-semibold">{meta.label}</p>
                        {e.severity && e.severity !== 'info' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider"
                            style={{ background: `${sevHex(e.severity)}18`, border: `1px solid ${sevHex(e.severity)}40`, color: sevHex(e.severity) }}>
                            {e.severity}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10.5px] text-white/50 flex-wrap">
                        <span className="inline-flex items-center gap-1"><User className="w-2.5 h-2.5" />by {e.actor}</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{e.timestamp ? new Date(e.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                        {e.ip && e.ip !== 'unknown' && <><span>·</span><span className="font-mono text-[9.5px] text-white/40">{e.ip}</span></>}
                      </div>
                      {Object.keys(details).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-[10px] text-cyan-300/70 cursor-pointer hover:text-cyan-300 select-none">Details</summary>
                          <pre className="mt-1.5 p-2 rounded-md bg-black/40 border border-white/[0.05] text-[10px] text-white/60 font-mono overflow-x-auto">{JSON.stringify(details, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ActionLogDrawer;
